import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {isEmpty} from '@shared/helpers/lang'
import {mapValues} from '@shared/helpers/object'

import {initBannerByAPI, initRewardBenefitByAPI} from '@web/actions/detail'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {APIStatus, BannerArea} from '@web/core/constants'
import {
    updateShouldMobileAreaPopupBannerRender,
    getInitialState,
    BannerState,
    convertBannerInfo,
} from '@web/slices/banner/slice'

import type {BannerReducers, MakeCreateSliceOptions} from './interfaces'

const makeSlice = (options?: MakeCreateSliceOptions) =>
    createSlice<BannerState, BannerReducers, 'banner', SliceSelectors<BannerState>>({
        name: 'banner',
        initialState: getInitialState(),
        reducers: {
            setShouldMobileAreaPopupBannerRender(state, {payload}) {
                state.shouldMobileAreaPopupBannerRender = payload
            },
        },
        extraReducers: (builder) => {
            builder
                .addCase(updateShouldMobileAreaPopupBannerRender, (state, {payload}) => {
                    state.shouldMobileAreaPopupBannerRender = payload
                })
                .addCase(initBannerByAPI.pending, (state) => {
                    state.status = APIStatus.FETCHING
                })
                .addCase(initBannerByAPI.fulfilled, (state, {payload}) => {
                    state.status = APIStatus.RESOLVED

                    if (isEmpty(payload)) {
                        return
                    }

                    state.bannerArea = convertBannerInfo(
                        mapValues(
                            (response) => ({
                                bannerType: response.bannerType,
                                exposureCardType: response.exposureCardType,
                                backgroundStyle: response.backgroundStyle,
                                description: response.description,
                                exposure: response.isExposure,
                                id: response.id,
                                exposureAreaTypes: response.exposureAreaTypes,
                                exposureChannelTypes: response.exposureChannelTypes,
                                exposurePageTypes: response.exposurePageTypes,
                                exposureRuleTypes: response.exposureRuleTypes,
                                linkUrl: response.linkUrl,
                                linkUrlForBrowsers: response.linkUrlForBrowsers,
                                nclickCode: response.nclickCode,
                                subtitle: response.subtitle,
                                subtitleStyle: response.subtitleStyle,
                                title: response.title,
                                titleStyle: response.titleStyle,
                                imageBackgroundStyle: response.imageBackgroundStyle,
                                imageFileName: response.imageFileName,
                                imageStyle: response.imageStyle,
                                cdnUrl: response.cdnUrl,
                                exposureSubAreaTypes: response.exposureSubAreaTypes,
                                cardCompanyCode: response.cardCompanyCode,
                                cardProductCodes: response.cardProductCodes,
                            }),
                            payload,
                        ),
                    )
                    state.expectedRewardByChangingPaymoney =
                        payload?.[BannerArea.ORDER_DETAIL_HEADER]?.meta?.membershipMeta?.expectedChargedPointPoint ?? 0
                })
                .addCase(initBannerByAPI.rejected, (state) => {
                    state.status = APIStatus.REJECTED
                })
                .addCase(initRewardBenefitByAPI.fulfilled, (state, {payload}) => {
                    state.expectedTotalRewardPointOfTodayOrders =
                        payload.expectNaverMembership?.expectTotalRewardPointOfTodayOrders ?? 0
                    state.expectedTotalRewardPointBySubscribing =
                        payload.expectNaverMembership?.expectRewardPointBySubscribing ?? 0
                })
            options?.extraReducers?.(builder)
            builder.addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                return payload.banner ?? state
            })
        },
    })

export default makeSlice
